import { canvasKit } from '@resources';
import type {
  Image as CKImage,
  Path as CKPath,
  Font,
  FontMgr,
  InputCommands,
  Paint,
  Paragraph,
  ParagraphBuilder,
  ParagraphStyle,
  Surface,
  Typeface
} from 'canvaskit';

// CanvasKit is based on Skia, which is a C++ library. When we create objects in
// CanvasKit, we are allocating memory in the C++ heap. If we don't free that
// memory, it will leak. This module provides functions to create and delete
// CanvasKit objects, and tracks the number of allocations to help identify
// leaks.
//
// When possible, prefer using the `with*` functions to ensure that objects are
// deleted when they are no longer needed. For example:
//
// ```ts
// withPaint(paint => {
//   paint.setColor(color);
//   canvas.drawRect(rect, paint);
// });
// ```
//
// This will ensure that the paint object is deleted even if an error is thrown
// inside the block.

// Set to `true` to enable periodic logging of CanvasKit allocations.
export const allocationsLogged = false;

// Set to `true` to restrict the number of cached CanvasKit allocations.
export const allocationsRestricted = false;

const allocations = new Map<string, number>();
const lastPrintedAllocations = new Map<string, number>();
const deleteLaterList: (() => void)[] = [];
let deleteLaterTimeoutId: ReturnType<typeof setTimeout> | undefined;

function deleteLater(fn: () => void) {
  deleteLaterList.push(fn);
  if (deleteLaterTimeoutId !== undefined) {
    clearTimeout(deleteLaterTimeoutId);
  }
  // Delete on the next tick to avoid deleting objects that are still being used
  // (particularly `Surface`).
  deleteLaterTimeoutId = setTimeout(deleteAllLater, 0);
}

function deleteAllLater() {
  if (deleteLaterList.length === 0) {
    return;
  }

  // console.log(`Deleting ${deleteLaterList.length} objects`);

  for (const fn of deleteLaterList) {
    fn();
  }
  deleteLaterList.length = 0;
  deleteLaterTimeoutId = undefined;
}

function printAllocations() {
  const allocationsChanged: boolean = Array.from(allocations).some(([name, count]) => {
    const lastCount = lastPrintedAllocations.get(name) ?? 0;
    return count !== lastCount;
  });
  if (allocationsChanged) {
    const strings = [];
    for (const [name, count] of allocations) {
      strings.push(`${name}: ${count.toString()}`);
    }
    console.log(`CanvasKit allocations: ${strings.join(', ')}`);

    lastPrintedAllocations.clear();
    for (const [name, count] of allocations) {
      lastPrintedAllocations.set(name, count);
    }
  }
}

export function startLoggingAllocations() {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (allocationsLogged) {
    setInterval(printAllocations, 1000);
  }
}

export function incrementAllocation(name: string) {
  const count = allocations.get(name) ?? 0;
  allocations.set(name, count + 1);
  // if (name == 'Surface') {
  //   console.log(`🟢 Incremented allocation for '${name}' to ${count + 1}`);
  //   // console.trace();
  // }
}

function decrementAllocation(name: string) {
  const count = allocations.get(name) ?? 0;
  if (count === 0) {
    throw new Error(`Allocation count for '${name}' is already zero.`);
  }
  // if (name == 'Surface') {
  //   console.log(`🔴 Decremented allocation for '${name}' to ${count - 1}`);
  // }
  allocations.set(name, count - 1);
}

export function makeSurface(element: HTMLCanvasElement): Surface {
  const newSurface = canvasKit.MakeWebGLCanvasSurface(element);
  if (!newSurface) {
    throw new Error('Failed to create surface');
  }
  incrementAllocation('Surface');
  return newSurface;
}

export function deleteSurface(surface: Surface) {
  deleteLater(() => {
    surface.delete();
    decrementAllocation('Surface');
  });
}

export function makePaint(): Paint {
  const paint = new canvasKit.Paint();
  paint.setAntiAlias(true);
  incrementAllocation('Paint');
  return paint;
}

export function deletePaint(paint: Paint) {
  paint.delete();
  decrementAllocation('Paint');
}

export function withPaint(fn: (paint: Paint) => void) {
  const paint = makePaint();
  try {
    fn(paint);
  } finally {
    deletePaint(paint);
  }
}

export function makePath(): CKPath {
  const path = new canvasKit.Path();
  incrementAllocation('Path');
  return path;
}

export function makePathFromSvgString(svgPath: string): CKPath {
  const path = canvasKit.Path.MakeFromSVGString(svgPath);
  if (!path) {
    throw new Error('Failed to create path from SVG string');
  }
  incrementAllocation('Path');
  return path;
}

export function makePathFromCmds(cmds: InputCommands): CKPath {
  const path = canvasKit.Path.MakeFromCmds(cmds);
  if (!path) {
    throw new Error('Failed to create path from commands');
  }
  incrementAllocation('Path');
  return path;
}

export function copyPath(path: CKPath): CKPath {
  const pathCopy = path.copy();
  incrementAllocation('Path');
  return pathCopy;
}

export function deletePath(path: CKPath) {
  path.delete();
  decrementAllocation('Path');
}

export function makeFont(face: Typeface | null, size?: number): Font {
  const font = new canvasKit.Font(face, size);
  incrementAllocation('Font');
  return font;
}

export function deleteFont(font: Font) {
  font.delete();
  decrementAllocation('Font');
}

export function makeFontMgr(fontBuffers: ArrayBuffer[]): FontMgr {
  const fontMgr = canvasKit.FontMgr.FromData(...fontBuffers);
  if (!fontMgr) {
    throw new Error('Failed to create font manager');
  }
  incrementAllocation('FontMgr');
  return fontMgr;
}

export function deleteFontMgr(fontMgr: FontMgr) {
  fontMgr.delete();
  decrementAllocation('FontMgr');
}

export function makeParagraphBuilder(
  style: ParagraphStyle,
  fontMgr: FontMgr
): ParagraphBuilder {
  const builder = canvasKit.ParagraphBuilder.Make(style, fontMgr);
  incrementAllocation('ParagraphBuilder');
  return builder;
}

export function deleteParagraphBuilder(builder: ParagraphBuilder) {
  builder.delete();
  decrementAllocation('ParagraphBuilder');
}

export function deleteParagraph(paragraph: Paragraph) {
  paragraph.delete();
  decrementAllocation('Paragraph');
}

export function trackParagraph(paragraph: Paragraph): Paragraph {
  incrementAllocation('Paragraph');
  return paragraph;
}

export function trackImage(image: CKImage): CKImage {
  incrementAllocation('Image');
  return image;
}

export function deleteImage(image: CKImage) {
  image.delete();
  decrementAllocation('Image');
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (allocationsRestricted) {
  console.warn('ParagraphCache is restricted.');
}
